import type { PageProps } from '~/@types/generic';
import type { Meta, Link } from '@unhead/vue';

export const useSeo = (props: PageProps) => {
    const appStore = useAppStore();
    const config = useRuntimeConfig();
    const route = useRoute();
    const { locale } = useLocales();

    const baseUrl = config.public.BASE_URL;

    const { defaultSeo } = storeToRefs(appStore);
    const title = computed(() => props.pageData?.seo?.title || defaultSeo.value?.title);
    const pageDescription = computed(() => props.pageData?.seo?.description || defaultSeo.value?.description);
    const description = computed(() => pageDescription.value || defaultSeo.value?.description);

    const link = computed<Link[]>(() => {
        const links: Link[] = [
            { rel: 'apple-touch-icon', sizes: '57x57', href: '/images/apple-touch-icon-57x57.png' },
            { rel: 'apple-touch-icon', sizes: '60x60', href: '/images/apple-touch-icon-60x60.png' },
            { rel: 'apple-touch-icon', sizes: '72x72', href: '/images/apple-touch-icon-72x72.png' },
            { rel: 'apple-touch-icon', sizes: '76x76', href: '/images/apple-touch-icon-76x76.png' },
            { rel: 'apple-touch-icon', sizes: '114x114', href: '/images/apple-touch-icon-114x114.png' },
            { rel: 'apple-touch-icon', sizes: '120x120', href: '/images/apple-touch-icon-120x120.png' },
            { rel: 'apple-touch-icon', sizes: '144x144', href: '/images/apple-touch-icon-144x144.png' },
            { rel: 'apple-touch-icon', sizes: '152x152', href: '/images/apple-touch-icon-152x152.png' },
            { rel: 'apple-touch-icon', sizes: '180x180', href: '/images/apple-touch-icon-180x180.png' },
            { rel: 'apple-touch-icon', href: '/images/apple-touch-icon.png' },
            { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/images/favicon-32x32.png' },
            { rel: 'icon', type: 'image/png', sizes: '16x16', href: '/images/favicon-16x16.png' },
            { rel: 'manifest', href: '/site.webmanifest' },
            { rel: 'mask-icon', href: '/safari-pinned-tab.svg', color: '#eb0613' },
        ];
        if (!Array.isArray(props.pageData?.cannonical) || !props.pageData?.url) return links;

        const pageUrlPath = props.pageData.url.url === '/' ? '' : props.pageData.url.url;
        links.push({
            rel: 'canonical',
            href: `${baseUrl}/${props.pageData.url.locale}${pageUrlPath}`,
        });

        const addAlternate = ({ href, hreflang }: { href: string; hreflang: string }) => {
            links.push({
                rel: 'alternate',
                hreflang,
                href,
            });
        };

        for (const cannonical of props.pageData.cannonical) {
            const canonicalLocale = cannonical.locale;
            if (!woomConfig.locales.find(({ code }) => code === canonicalLocale)) continue;

            const urlPath = cannonical.url === '/' ? '' : cannonical.url;
            const href = `${baseUrl}/${canonicalLocale}${urlPath}`;
            const [lang, region] = canonicalLocale.split('_');
            if (region?.toUpperCase() === 'INT') {
                addAlternate({
                    hreflang: lang,
                    href,
                });
                // https://developers.google.com/search/docs/specialty/international/localized-versions#html
                // set the x-default pointing to the INT page
                addAlternate({
                    hreflang: 'x-default',
                    href,
                });
                continue;
            }

            addAlternate({
                hreflang: canonicalLocale.replace('_', '-'),
                href,
            });
            if (canonicalLocale.toLowerCase() === 'en_us') {
                addAlternate({
                    hreflang: 'en-CA',
                    href,
                });
            }
        }
        return links;
    });

    const socialImage = computed(() => {
        const imageURL = props.pageData?.page?.product?.shopImage;
        const imageData = imageURL ? defaultSeo.value?.image : props.pageData?.seo?.image || defaultSeo.value?.image;

        if (!imageData?.id || typeof imageData === 'string') return imageURL;
        if (isAssetKontentAi(imageData)) return `${imageData.data.src}?w=800`;
        if (isAssetBynder(imageData)) return `${imageData.data.baseUrl || ''}${imageData.data.image}`;
    });

    const meta = computed<Meta[]>(() => {
        const meta: Meta[] = [
            {
                property: 'woom:base',
                content: baseUrl,
            },
            {
                property: 'og:url',
                content: `${baseUrl}${route.fullPath}`,
            },
            {
                property: 'og:locale',
                content: locale.value,
            },
            {
                property: 'msapplication-TileColor',
                content: '#ffffff',
            },
            {
                property: 'theme-color',
                content: '#ffffff',
            },
        ];

        if (title.value) {
            meta.push({ property: 'og:title', content: title.value });
            meta.push({ name: 'twitter:title', content: title.value });
        }
        if (description.value) {
            meta.push({ name: 'description', content: description.value });
            meta.push({
                property: 'og:description',
                content: description.value,
            });
            meta.push({
                name: 'twitter:description',
                content: description.value,
            });
        }
        if (socialImage.value) {
            meta.push({ property: 'og:image', content: socialImage.value });
            meta.push({
                name: 'twitter:image',
                content: socialImage.value,
            });
        }
        return meta;
    });

    useHead({
        title: title.value,
        htmlAttrs: {
            lang: locale.value.replace('_', '-'),
        },
        meta,
        link,
    });
};
